import React from 'react';
import { TFunction } from 'i18next';
import { ADMIN_PAGES_KEYS } from '../../../../i18n/constants/keys';

export const TooltipWrapper: React.FC<{ text: string }> = ({ text }) => {
  return (
    <div className='relative group w-full flex items-center justify-start text-left'>
      <div className='max-w-[180px] truncate whitespace-nowrap overflow-hidden'>
        { text || '-' }
      </div>
      <div className='absolute z-[9999] hidden group-hover:flex flex-col items-center'>
        <div className='relative'>
          <div className='absolute bottom-full  px-3 py-2 text-sm text-white bg-gray-900 rounded-md shadow-lg w-max max-w-xs whitespace-normal'>
            { text }
          </div>
        </div>
      </div>
    </div>
  );
};

const unixToCurrentTimezone = (timestamp: number, fullFormat: boolean) => {
  if (!timestamp) return '-';
  const date = new Date(timestamp * 1000);
  return fullFormat ? date.toLocaleString() : date.toLocaleDateString();
};

export const getColumns = (t: TFunction) => [
  {
    key: 'jobTitle',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.jobTitle),
    width: '250px',
    align: 'left' as const,
    render: (row: any) => <TooltipWrapper text={row.jobTitle} />,
    disableSort: false,
  },
  {
    key: 'jobReference',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.jobReference),
    width: '220px',
    align: 'left' as const,
    render: (row: any) => <TooltipWrapper text={row.jobReference} />,
    disableSort: true,
  },
  {
    key: 'hiringManager',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.hiringManager),
    width: '200px',
    align: 'left' as const,
    render: (row: any) => (
      <TooltipWrapper text={(row.hiringManager as string[])?.join(', ')} />
    ),
    disableSort: true,
  },
  {
    key: 'internalRecruiter',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.internalRecruiter),
    width: '200px',
    align: 'left' as const,
    render: (row: any) => (
      <TooltipWrapper text={(row.internalRecruiter as string[])?.join(', ')} />
    ),
    disableSort: true,
  },
  {
    key: 'applicants',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.applicants),
    width: '150px',
    align: 'left' as const,
    render: (row: any) => <TooltipWrapper text={row.applicants.toString()} />,
    disableSort: true,
  },
  {
    key: 'creationDate',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.creationDate),
    width: '200px',
    align: 'left' as const,
    render: (row: any) => (
      <div className='flex justify-start text-left'>
        { unixToCurrentTimezone(row.creationDate, false) }
      </div>
    ),
    disableSort: false,
  },
  {
    key: 'expiryDate',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.expiryDate),
    width: '200px',
    align: 'left' as const,
    render: (row: any) => (
      <div className='flex justify-start text-left'>
        { unixToCurrentTimezone(row.expiryDate, false) }
      </div>
    ),
    disableSort: false,
  },
  {
    key: 'tags',
    label: t(ADMIN_PAGES_KEYS.jobOpenings.list.table.tags),
    width: 'auto',
    align: 'left' as const,
    render: (row: any) => {
      const visibleTags = row.tags?.slice(0, 3) || [];
      const remainingTags = row.tags?.slice(3) || [];

      return (
        <div className='flex gap-1 justify-start items-center text-left'>
          { row.tags && row.tags.length > 0 ? (
            <>
              { visibleTags.map((tag: string, index: number) => (
                <span
                    key={index}
                    className='flex items-center uppercase px-2 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-100/50 shadow-sm'
                >
                  { tag }
                </span>
              )) }
              { remainingTags.length > 0 && (
                <div className='relative group ml-1'>
                  <span
                      title={remainingTags.join(', ')}
                      className='flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-500 border border-slate-200 cursor-help transition-all hover:bg-slate-200 hover:text-slate-700'
                  >
                    +{ remainingTags.length }
                  </span>
                  <div className='absolute z-[10000000] invisible group-hover:visible flex flex-col items-center bottom-[120%] left-1/2 -translate-x-1/2 pointer-events-none transition-all duration-200'>
                    <div className='relative'>
                      <div className='bg-gray-900 text-white text-[11px] px-3 py-2 rounded-lg shadow-2xl w-max max-w-[200px] leading-relaxed border border-gray-800 flex flex-col gap-1'>
                        <div className='flex flex-wrap gap-1'>
                          { remainingTags.map((tag: string, i: number) => (
                            <span key={i} className='text-emerald-400 shrink-0'>
                              { tag }
                              { i < remainingTags.length - 1 ? ',' : '' }
                            </span>
                          )) }
                        </div>
                      </div>
                      <div className='absolute bottom-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900' />
                    </div>
                  </div>
                </div>
              ) }
            </>
          ) : (
            <span className='text-gray-400 text-xs italic'>
              { t(ADMIN_PAGES_KEYS.jobOpenings.list.table.noTags) }
            </span>
          ) }
        </div>
      );
    },
    disableSort: false,
  },
];
